import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Users, Plus, Send, Settings, UserPlus, LogOut, Crown, Search,
  Paperclip, X, FileText, Image, File, Download, Loader2, Bot, Building2, Coins,
  Trophy, Bell, Shield, Globe, Languages
} from "lucide-react";
import { format } from "date-fns";
import AIPalChat from "@/components/AIPalChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGE_NAMES = {
  "en": "English",
  "fr": "French",
  "es": "Spanish",
  "pt": "Portuguese",
  "zh": "Mandarin",
  "ar": "Arabic",
  "de": "German",
  "it": "Italian",
  "ru": "Russian",
  "ja": "Japanese",
  "ko": "Korean",
  "hi": "Hindi",
  "sw": "Swahili",
  "zu": "Zulu",
  "af": "Afrikaans",
  "xh": "Xhosa",
  "nl": "Dutch",
  "pl": "Polish",
  "tr": "Turkish",
  "vi": "Vietnamese",
  "th": "Thai"
};

export default function GroupChat() {
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserLang, setCurrentUserLang] = useState("en");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", description: "", group_type: "social", access_fee: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showGroupspal, setShowGroupspal] = useState(false);
  const [groupFilter, setGroupFilter] = useState("all"); // "all", "social", "corporate"
  const [showCorporateInfo, setShowCorporateInfo] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);

  const [corporateStatus, setCorporateStatus] = useState({ is_corporate: false });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchGroups();
    fetchCurrentUser();
    fetchCorporateStatus();
  }, []);

  const fetchCorporateStatus = async () => {
    try {
      const res = await axios.get(`${API}/alerts/corporate/status`, { headers: getHeaders() });
      setCorporateStatus(res.data);
    } catch (err) {
      setCorporateStatus({ is_corporate: false });
    }
  };

  useEffect(() => {
    if (activeGroup) {
      fetchMessages(activeGroup.id);
    }
  }, [activeGroup]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // WebSocket connection for real-time group messages
  useEffect(() => {
    if (!currentUserId) return;

    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsBase = baseUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProtocol}://${wsBase}/ws/comms/${currentUserId}`;

    let reconnectTimer = null;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "new_group_message") {
            if (data.group_id === activeGroup?.id) {
              setMessages((prev) => [...prev, data.message]);
            }
            fetchGroups();
          }
        } catch {}
      };

      ws.onclose = () => {
        clearInterval(ws._pingInterval);
        wsRef.current = null;
        reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [currentUserId, activeGroup?.id]);

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setCurrentUserId(res.data.user_id);
      setCurrentUserLang(res.data.default_language || "en");
    } catch (err) {
      console.error("Failed to fetch user");
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/comms/groups`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setGroups(res.data);
    } catch (err) {
      toast.error("Failed to load groups");
    }
  };

  const fetchMessages = async (groupId) => {
    try {
      const res = await axios.get(`${API}/comms/groups/${groupId}/messages`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setMessages(res.data);
    } catch (err) {
      toast.error("Failed to load messages");
    }
  };

  const createGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Group name is required");
      return;
    }
    
    // Corporate groups require corporate subscription
    if (newGroup.group_type === "corporate" && !corporateStatus.is_corporate) {
      toast.error("Corporate subscription required to create corporate groups");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/comms/groups`, newGroup, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success(`${newGroup.group_type === "corporate" ? "Corporate g" : "G"}roup created`);
      setIsCreateOpen(false);
      setNewGroup({ name: "", description: "", group_type: "social", access_fee: 0 });
      fetchGroups();
      setActiveGroup(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create group");
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !activeGroup) return;
    
    try {
      const res = await axios.post(
        `${API}/comms/groups/${activeGroup.id}/messages`,
        { content: newMessage, attachments: attachments },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      setMessages([...messages, res.data]);
      setNewMessage("");
      setAttachments([]);
    } catch (err) {
      toast.error("Failed to send message");
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(`${API}/comms/chat/upload`, formData, {
          headers: getHeaders(),
          withCredentials: true
        });
        
        setAttachments(prev => [...prev, res.data]);
      }
      toast.success(`${files.length} file(s) attached`);
    } catch (err) {
      toast.error("Failed to upload file");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const getFileIcon = (type) => {
    if (type?.startsWith("image/")) return Image;
    if (type?.includes("pdf") || type?.includes("document")) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/comms/chat/users/search?q=${encodeURIComponent(query)}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setSearchResults(res.data);
    } catch (err) {
      toast.error("Search failed");
    }
  };

  const addMember = async (memberId) => {
    try {
      await axios.post(`${API}/comms/groups/${activeGroup.id}/members`, 
        { member_id: memberId },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      toast.success("Member added");
      fetchGroups();
      setSearchQuery("");
      setSearchResults([]);
      // Refresh active group
      const res = await axios.get(`${API}/comms/groups/${activeGroup.id}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setActiveGroup(res.data);
    } catch (err) {
      toast.error("Failed to add member");
    }
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API}/comms/groups/${activeGroup.id}/members/${memberId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Member removed");
      fetchGroups();
      // Refresh active group
      const res = await axios.get(`${API}/comms/groups/${activeGroup.id}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setActiveGroup(res.data);
    } catch (err) {
      toast.error("Failed to remove member");
    }
  };

  const leaveGroup = async () => {
    try {
      await axios.post(`${API}/comms/groups/${activeGroup.id}/leave`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Left group");
      setActiveGroup(null);
      setIsSettingsOpen(false);
      fetchGroups();
    } catch (err) {
      toast.error("Cannot leave group (you may be the creator)");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getSenderName = (senderId) => {
    if (!activeGroup?.member_details) return "Unknown";
    const member = activeGroup.member_details.find(m => m.user_id === senderId);
    return member?.name || "Unknown";
  };

  const isAdmin = activeGroup?.admins?.includes(currentUserId);
  const isCreator = activeGroup?.creator_id === currentUserId;

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-app bg-app-card px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-app">
            GROUP CHATS
          </h1>
          <p className="text-sm text-app-secondary">Team conversations</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Language Selector */}
          <Select
            value={currentUserLang}
            onValueChange={async (v) => {
              setCurrentUserLang(v);
              try {
                await axios.put(`${API}/auth/language`, { language: v }, {
                  headers: getHeaders(), withCredentials: true,
                });
              } catch {}
            }}
          >
            <SelectTrigger className="h-9 w-36 text-xs rounded-none border-app" data-testid="groupchat-lang-select">
              <Languages className="w-3.5 h-3.5 text-app-secondary" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-app-card rounded-none max-h-60">
              {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
                <SelectItem key={code} value={code} className="text-sm">{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={() => setShowGroupspal(!showGroupspal)}
            variant={showGroupspal ? "default" : "outline"}
            className={showGroupspal ? "bg-blue-500 hover:bg-blue-600" : ""}
            data-testid="groupspal-toggle"
          >
            <Bot className="w-4 h-4 mr-2" />
            Groupspal
          </Button>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-500 hover:bg-violet-600 rounded-lg" data-testid="create-group-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Group
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-app-card">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed']">Create New Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Group Type</Label>
                <Select
                  value={newGroup.group_type}
                  onValueChange={(v) => setNewGroup({ ...newGroup, group_type: v, access_fee: v === "social" ? 0 : newGroup.access_fee })}
                >
                  <SelectTrigger className="mt-1" data-testid="group-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Social Group (Free)
                      </div>
                    </SelectItem>
                    <SelectItem value="corporate" disabled={!corporateStatus.is_corporate}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Corporate Group (10 AI)
                        {!corporateStatus.is_corporate && <Badge variant="outline" className="ml-2 text-xs">Requires Corporate</Badge>}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {newGroup.group_type === "corporate" && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Coins className="w-3 h-3" />
                     Creating a corporate group costs 10 AI
                  </p>
                )}
              </div>
              
              <div>
                <Label>Group Name</Label>
                <Input
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="Enter group name"
                  className="mt-1"
                  data-testid="group-name-input"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                  placeholder="What is this group about?"
                  className="mt-1"
                  data-testid="group-description-input"
                />
              </div>
              
              {newGroup.group_type === "corporate" && (
                <div>
                  <Label>Access Fee (AI)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newGroup.access_fee}
                    onChange={(e) => setNewGroup({ ...newGroup, access_fee: parseInt(e.target.value) || 0 })}
                    placeholder="0 = Free access"
                    className="mt-1"
                    data-testid="group-access-fee-input"
                  />
                  <p className="text-xs text-app-secondary mt-1">
                    Members will pay this amount to join the group
                  </p>
                </div>
              )}
              
              <Button onClick={createGroup} className="w-full bg-violet-500 hover:bg-violet-600" data-testid="save-group-btn">
                {newGroup.group_type === "corporate" ? (
                  <>
                    <Building2 className="w-4 h-4 mr-2" />
                     Create Corporate Group (10 AI)
                  </>
                ) : (
                  "Create Group"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Groups List */}
        <div className="w-80 border-r border-app bg-app-card flex flex-col">
          {/* Group Type Filter Tabs */}
          <div className="p-3 border-b border-app">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={groupFilter === "all" ? "default" : "outline"}
                onClick={() => setGroupFilter("all")}
                className="flex-1"
                data-testid="filter-all-groups"
              >
                All
              </Button>
              <Button
                size="sm"
                variant={groupFilter === "social" ? "default" : "outline"}
                onClick={() => setGroupFilter("social")}
                className="flex-1 bg-violet-500 hover:bg-violet-600"
                data-testid="filter-social-groups"
              >
                <Users className="w-4 h-4 mr-1" />
                Social
              </Button>
              <Button
                size="sm"
                variant={groupFilter === "corporate" ? "default" : "outline"}
                onClick={() => { setGroupFilter("corporate"); setShowCorporateInfo(true); }}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                data-testid="filter-corporate-groups"
              >
                <Building2 className="w-4 h-4 mr-1" />
                Corporate
              </Button>
            </div>
          </div>
          
          <ScrollArea className="flex-1">
            {groups.filter(g => groupFilter === "all" || g.group_type === groupFilter || (!g.group_type && groupFilter === "social")).length === 0 ? (
              <div className="p-6 text-center">
                <Users className="w-12 h-12 text-app-secondary mx-auto mb-3" />
                <p className="text-app-secondary text-sm">No {groupFilter !== "all" ? groupFilter : ""} groups yet</p>
                <p className="text-app-secondary text-xs">Create a group to start collaborating</p>
              </div>
            ) : (
              groups
                .filter(g => groupFilter === "all" || g.group_type === groupFilter || (!g.group_type && groupFilter === "social"))
                .map((group) => (
                <button
                  key={group.id}
                  onClick={() => setActiveGroup(group)}
                  className={`w-full flex items-center gap-3 p-4 border-b border-app hover:bg-app text-left transition-colors ${
                    activeGroup?.id === group.id ? 'bg-violet-50' : ''
                  }`}
                  data-testid={`group-${group.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    group.group_type === "corporate" 
                      ? "bg-gradient-to-br from-amber-400 to-orange-500" 
                      : "bg-gradient-to-br from-violet-400 to-purple-500"
                  }`}>
                    {group.group_type === "corporate" ? (
                      <Building2 className="w-5 h-5 text-white" />
                    ) : (
                      <Users className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-app truncate">{group.name}</p>
                      {group.group_type === "corporate" && (
                        <Badge className="bg-amber-500 text-xs px-1.5 py-0">Corp</Badge>
                      )}
                      {group.unread_count > 0 && (
                        <span className="bg-violet-500 text-white text-xs px-2 py-0.5 rounded-full ml-auto">
                          {group.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-app-secondary truncate">
                      {group.member_details?.length || 0} members
                      {group.access_fee > 0 && ` • ${group.access_fee} ${group.access_fee === 1 ? "AI" : "AIs"} to join`}
                    </p>
                  </div>
                </button>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-app">
          {activeGroup ? (
            <>
              {/* Chat Header */}
              <div className="bg-app-card border-b border-app p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-purple-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-app">{activeGroup.name}</p>
                    <p className="text-xs text-app-secondary">
                      {activeGroup.member_details?.length || 0} members
                    </p>
                  </div>
                </div>
                
                <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="group-settings-btn">
                      <Settings className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-app-card max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-['Barlow_Condensed']">Group Settings</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <p className="font-medium text-sm text-app">{activeGroup.name}</p>
                        <p className="text-xs text-app-secondary">{activeGroup.description || "No description"}</p>
                      </div>
                      
                      {isAdmin && (
                        <div>
                          <Label className="text-sm">Add Member</Label>
                          <div className="relative mt-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-secondary" />
                            <Input
                              placeholder="Search users..."
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                searchUsers(e.target.value);
                              }}
                              className="pl-9"
                              data-testid="add-member-search"
                            />
                          </div>
                          {searchResults.length > 0 && (
                            <div className="mt-2 border rounded-lg max-h-32 overflow-auto">
                              {searchResults.map((user) => (
                                <button
                                  key={user.user_id}
                                  onClick={() => addMember(user.user_id)}
                                  className="w-full flex items-center gap-2 p-2 hover:bg-app text-left text-sm"
                                >
                                  <UserPlus className="w-4 h-4 text-violet-500" />
                                  {user.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-sm">Members</Label>
                        <div className="mt-2 space-y-2 max-h-48 overflow-auto">
                          {activeGroup.member_details?.map((member) => (
                            <div key={member.user_id} className="flex items-center justify-between p-2 bg-app rounded-lg">
                              <div className="flex items-center gap-2">
                                <Avatar className="w-6 h-6">
                                  <AvatarImage src={member.picture} />
                                  <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm">{member.name}</span>
                                {activeGroup.admins?.includes(member.user_id) && (
                                  <Crown className="w-3 h-3 text-amber-500" />
                                )}
                              </div>
                              {isAdmin && member.user_id !== activeGroup.creator_id && member.user_id !== currentUserId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeMember(member.user_id)}
                                  className="text-red-500 hover:text-red-600 h-6 px-2"
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {!isCreator && (
                        <Button
                          variant="outline"
                          onClick={leaveGroup}
                          className="w-full text-red-500 border-red-200 hover:bg-red-50"
                          data-testid="leave-group-btn"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Leave Group
                        </Button>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === currentUserId;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? '' : 'flex gap-2'}`}>
                          {!isOwn && (
                            <Avatar className="w-6 h-6 mt-1">
                              <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                                {getInitials(getSenderName(msg.sender_id))}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div
                            className={`rounded-2xl px-4 py-2 ${
                              isOwn
                                ? 'bg-violet-500 text-white rounded-br-md'
                                : 'bg-app-card border border-app text-app rounded-bl-md'
                            }`}
                          >
                            {!isOwn && (
                              <p className="text-xs font-medium text-violet-600 mb-1">
                                {getSenderName(msg.sender_id)}
                              </p>
                            )}
                            {/* Translation indicator for group messages */}
                            {!isOwn && msg.original_language && msg.translations && msg.translations[currentUserLang] && msg.original_language !== currentUserLang && (
                              <div className="flex items-center gap-1 text-xs text-amber-600 mb-1 pb-1 border-b border-app">
                                <Globe className="w-3 h-3" />
                                <span>Translated from {LANGUAGE_NAMES[msg.original_language] || msg.original_language}</span>
                              </div>
                            )}
                            {msg.content && (
                              <p className="text-sm">
                                {!isOwn && msg.translations && msg.translations[currentUserLang]
                                  ? msg.translations[currentUserLang]
                                  : msg.content}
                              </p>
                            )}
                            
                            {/* Attachments */}
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className={`space-y-2 ${msg.content ? 'mt-2' : ''}`}>
                                {msg.attachments.map((att, idx) => {
                                  const FileIcon = getFileIcon(att.type);
                                  const isImage = att.type?.startsWith("image/");
                                  return (
                                    <div key={idx}>
                                      {isImage ? (
                                        <a 
                                          href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block"
                                        >
                                          <img 
                                            src={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                            alt={att.name}
                                            className="max-w-full rounded-lg max-h-48 object-cover"
                                          />
                                        </a>
                                      ) : (
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`flex items-center gap-2 p-2 rounded-lg ${
                                            isOwn ? 'bg-violet-400/30' : 'bg-slate-100'
                                          }`}
                                        >
                                          <FileIcon className="w-4 h-4 flex-shrink-0" />
                                          <div className="min-w-0 flex-1">
                                            <p className="text-xs font-medium truncate">{att.name}</p>
                                            <p className={`text-xs ${isOwn ? 'text-violet-200' : 'text-app-secondary'}`}>
                                              {formatFileSize(att.size)}
                                            </p>
                                          </div>
                                          <Download className="w-4 h-4 flex-shrink-0" />
                                        </a>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            <p className={`text-xs mt-1 ${isOwn ? 'text-violet-200' : 'text-app-secondary'}`}>
                              {format(new Date(msg.created_at), 'HH:mm')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="bg-app-card border-t border-app p-4">
                {/* Attachment Preview */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {attachments.map((att) => {
                      const FileIcon = getFileIcon(att.type);
                      return (
                        <div 
                          key={att.id}
                          className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2"
                        >
                          <FileIcon className="w-4 h-4 text-app-secondary" />
                          <span className="text-sm text-app max-w-[150px] truncate">{att.name}</span>
                          <button
                            onClick={() => removeAttachment(att.id)}
                            className="text-app-secondary hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    multiple
                    data-testid="group-file-input"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="rounded-full w-10 h-10 p-0"
                    data-testid="group-attach-file-btn"
                  >
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Paperclip className="w-4 h-4" />
                    )}
                  </Button>
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    className="flex-1 bg-app border-app rounded-full bg-surface-input text-app placeholder:text-placeholder"
                    data-testid="group-message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() && attachments.length === 0}
                    className="rounded-full w-10 h-10 p-0 bg-violet-500 hover:bg-violet-600"
                    data-testid="send-group-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Users className="w-16 h-16 text-app-secondary mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-app">Select a group</h3>
                <p className="text-app-secondary text-sm">or create a new group to start collaborating</p>
              </div>
            </div>
          )}
        </div>

        {/* Groupspal Panel */}
        {showGroupspal && (
          <div className="w-96 border-l border-app">
            <AIPalChat
              palId="groupspal"
              palName="Groupspal"
              palIcon={Users}
              color="from-blue-500 to-indigo-500"
              onClose={() => setShowGroupspal(false)}
            />
          </div>
        )}
        
        {/* Corporate Groups Info Dialog */}
        <Dialog open={showCorporateInfo} onOpenChange={setShowCorporateInfo}>
          <DialogContent className="bg-app-card max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2 text-amber-600">
                <Building2 className="w-6 h-6" />
                Corporate Groups
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-amber-50 rounded-lg p-4">
                <h3 className="font-semibold text-amber-800 mb-2">What are Corporate Groups?</h3>
                <p className="text-sm text-amber-700">
                  Corporate Groups are premium collaboration spaces designed for businesses and organizations.
                  They offer enhanced features and monetization options for professional use.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-app mb-3">Benefits for Organizations:</h3>
                <ul className="space-y-2 text-sm text-app">
                  <li className="flex items-start gap-2">
                    <Coins className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Monetize Access:</strong> Charge members an access fee (in AI) to join your group</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Trophy className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Run Competitions:</strong> Create reward-based activities and competitions within your group</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Bell className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Auto Alerts:</strong> New corporate groups are automatically promoted in the AlertsHub</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Premium Badge:</strong> Stand out with the Corporate badge and gold styling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <span><strong>Admin Controls:</strong> Enhanced moderation and member management tools</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-100 rounded-lg p-4">
                <h3 className="font-semibold text-app mb-2">Requirements:</h3>
                <ul className="text-sm text-app space-y-1">
                  <li>• Corporate Subscription (100 AI/month)</li>
                  <li>• 10 AI to create each corporate group</li>
                </ul>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCorporateInfo(false)}>
                  Close
                </Button>
                {!corporateStatus.is_corporate && (
                  <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => {
                    setShowCorporateInfo(false);
                    setIsCreateOpen(true);
                    setNewGroup({ ...newGroup, group_type: "corporate" });
                  }}>
                    <Building2 className="w-4 h-4 mr-2" />
                    Create Corporate Group
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
